import { useEffect, useMemo } from 'react';
import { getProjectKeyOrId } from '@jira/router-resources__utils/src/getProjectKeyOrId.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	WORK_CATEGORIES_QUEUE_SUPPORTED,
	DEVELOPER_ESCALATION,
	CASES,
} from '@jira/servicedesk__work-category/src/practiceKeyJqlNameMapping.constants.tsx';
import type {
	WorkCategoriesQueueSupported,
	WorkCategoriesApiResource,
	WorkCategoryApiResource,
} from '@jira/servicedesk__work-category/src/WorkCategoryApiResource.types.tsx';
import type { DataState } from '@jira/platform__common-services/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createResource, useResource, type RouteResource } from '@atlassian/react-resource-router';

const RESOURCE_TYPE_SERVICEDESK_PRACTICES = 'SERVICEDESK_PRACTICES';

const ONE_DAY_IN_MILLIS = 60 * 60 * 1000 * 24;

type ResourceData = {
	practices: {
		key: WorkCategoryApiResource;
	}[];
};

export const servicedeskPracticesResource: RouteResource<ResourceData> =
	createResource<ResourceData>({
		type: RESOURCE_TYPE_SERVICEDESK_PRACTICES,
		getKey: ({ match }) => getProjectKeyOrId(match),
		getDataLoader: () =>
			import(
				/* webpackChunkName: "async-resource-servicedesk-practices", jiraSpaEntry: "async-resource-servicedesk-practices" */ './getData.tsx'
			),
		maxAge: ONE_DAY_IN_MILLIS,
	});

const getQueuesOrder = (): WorkCategoriesQueueSupported => {
	return fg('jsm-case-management')
		? WORK_CATEGORIES_QUEUE_SUPPORTED
		: WORK_CATEGORIES_QUEUE_SUPPORTED.filter((p) => p !== CASES);
};

const getWorkCategoryOrder = (): WorkCategoriesApiResource => [
	...getQueuesOrder(),
	DEVELOPER_ESCALATION,
];

const transformItsmPracticesList = (
	data: ResourceData | null,
): WorkCategoriesQueueSupported | null => {
	if (!data?.practices) return null;

	const queuesOrder = getQueuesOrder();
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	return queuesOrder.filter((practice: any) =>
		data.practices.some((practiceInData) => practiceInData.key === practice),
	);
};

const transformServiceDeskPracticesList = (
	data: ResourceData | null,
): WorkCategoriesApiResource | null => {
	if (!data?.practices) return null;

	const workCategoryOrder = getWorkCategoryOrder();
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	return workCategoryOrder.filter((practice: any) =>
		data.practices.some((practiceInData) => practiceInData.key === practice),
	);
};

// If you're using this hook in your route please make sure you add servicedeskPracticesResource to
// your route definition e.g.
// {
//    path: `/projects/.../queues`
//    resources: [ servicedeskPracticesResource ],
//    ...
// }
export const useWorkCategoriesQueueSupported = (): DataState<WorkCategoriesQueueSupported> => {
	const { data, loading, ...resource } = useResource<ResourceData>(servicedeskPracticesResource);
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const error = resource.error as Error;

	return useMemo(
		() => ({
			data: transformItsmPracticesList(data),
			loading,
			error,
		}),
		[data, error, loading],
	);
};

export const useWorkCategoriesAllSupported = (): DataState<WorkCategoriesApiResource> => {
	const { data, loading, ...resource } = useResource<ResourceData>(servicedeskPracticesResource);
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const error = resource.error as Error;

	return useMemo(
		() => ({
			data: transformServiceDeskPracticesList(data),
			loading,
			error,
		}),
		[data, error, loading],
	);
};

// This is for use where the practices resource may or may not be a route resource (eg in a nav component), but we need
// to eventually have the data anyway. We will force a refresh the first time the component with this hook is mounted.
// Only for use when you don't rely on this resource for SSR or initial render.
export const useWorkCategoriesAllSupportedWithForceRefreshOnMount: () => DataState<WorkCategoriesApiResource> =
	() => {
		const {
			data,
			loading,
			refresh: refreshPractices,
			...resource
		} = useResource<ResourceData>(servicedeskPracticesResource);
		// oxlint-disable-next-line typescript/consistent-type-assertions
		const error = resource.error as Error;

		useEffect(() => {
			if (!loading && !data && !error) {
				refreshPractices();
			}
		}, [data, error, loading, refreshPractices]);

		return useMemo(
			() => ({
				data: transformServiceDeskPracticesList(data),
				loading,
				error,
			}),
			[data, error, loading],
		);
	};
